// Pure helpers for the readers views (piece tab, Live list, Desk card). Nothing here may produce NaN.

export type Access = { member: number; signed_in: number; anonymous: number; not_recorded: number }
export type DayViews = { date: string; views: number }
export type ArticleMetrics = {
  article_id: number
  window_days: number
  tracking_since: string | null
  views: number
  /** Identified visitors plus private readers. */
  people: number
  /** Page views from readers whose browser sends a privacy setting (GPC/DNT): counted, never identified. */
  private_readers?: number
  by_day: DayViews[]
  access: Access
  read_half_pct: number
  finished_pct: number | null
  median_seconds: number
  paywall_views: number
  read_full_clicks: number
  signups_after: number
  channels: { channel: string; visits: number }[]
  member_reads: { opens: number; read_half: number; finished: number }
  search: { impressions: number; clicks: number; avg_position: number | null }
  /** Visitors Desk numbers for the same window; absent or null while the desk source is off. */
  desk?: DeskReaders | null
}
export type DeskTotals = {
  people: number; views: number; real_reads: number; read_end_rate: number | null; recirculation_rate: number | null
  entries: number; paywall_hits: number; paywall_rate: number | null; members_gained: number
  who: { anonymous: number; known: number; member: number }
}
export type DeskReaders = {
  window: { from: string; to: string; days: number }
  totals: DeskTotals
  today: { people: number; views: number; real_reads: number }
  href: string
  daily?: { day: string; people: number; views: number }[]
  sources?: { channel: string; label: string; visits: number }[]
}
export type PieceCopy = { where: 'free' | 'members'; article_id: number; label: string; metrics: ArticleMetrics }
export type PieceMetrics = {
  piece_id: number
  window_days: number
  tracking_since: string | null
  /** When the piece went live (ISO), or null while it has not. */
  published_at?: string | null
  copies: PieceCopy[]
  note?: string
}
export type LiveReaders = {
  people: number; finished_pct: number | null; members_pct: number; signups_after: number
  members_gained?: number; source?: 'desk' | 'raw'
}

export const WINDOWS = [7, 30, 90] as const
export type Window = (typeof WINDOWS)[number]
/** The plural name of the members' copy (server twin: MEMBERS_COPY_LABEL in content/sections.py). */
export const MEMBERS_COPY_LABEL = 'Member exclusives'
export const EMPTY_TEXT = 'No readers yet. Numbers appear within a few minutes of the first visit.'
export const NO_SEARCH_TEXT = 'No search data for this piece yet'

const num = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) ? n : 0)

/** "2 min 5 s", or "45 s" under a minute. */
export function formatDuration(seconds: number): string {
  const t = Math.max(0, Math.round(num(seconds)))
  const m = Math.floor(t / 60)
  const s = t % 60
  return m > 0 ? `${m} min ${s} s` : `${s} s`
}

/** A whole-number percentage: "42%". */
export function pct(n: number): string {
  return `${Math.round(num(n))}%`
}

/** A percentage, or a dash when there is no rate (under the minimum base). */
export function pctOrDash(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : pct(n)
}

const CHANNELS: Record<string, string> = {
  search: 'Search', email: 'Email', social: 'Social', direct: 'Direct', referral: 'Other sites', other: 'Other',
}
export function channelLabel(key: string): string {
  return CHANNELS[key] ?? 'Other'
}

export type Segment = { key: keyof Access; label: string; count: number; pct: number }
const GROUPS: { key: keyof Access; label: string }[] = [
  { key: 'member', label: 'Members' }, { key: 'signed_in', label: 'Signed in' },
  { key: 'anonymous', label: 'Visitors' }, { key: 'not_recorded', label: 'Not recorded' },
]

/** The stacked bar: whole-number shares that sum to exactly 100 (largest remainder), or no segments at all. */
export function groupBar(access: Access): Segment[] {
  const counts = GROUPS.map((g) => Math.max(0, num(access[g.key])))
  const total = counts.reduce((a, b) => a + b, 0)
  if (total === 0) return []
  const exact = counts.map((c) => (c * 100) / total)
  const pcts = exact.map(Math.floor)
  let left = 100 - pcts.reduce((a, b) => a + b, 0)
  const order = exact.map((e, i) => ({ i, r: e - Math.floor(e) })).sort((a, b) => b.r - a.r || a.i - b.i)
  for (const { i } of order) { if (left <= 0) break; if (counts[i] > 0) { pcts[i] += 1; left -= 1 } }
  return GROUPS.map((g, i) => ({ key: g.key, label: g.label, count: counts[i], pct: pcts[i] })).filter((s) => s.count > 0)
}

/** The copy switch label. */
export function bothLabel(where: 'free' | 'members', sectionLabel: string): string {
  return where === 'members' ? MEMBERS_COPY_LABEL : `${sectionLabel} summary`
}

/** "1 person", "2 people". */
export function people(n: number): string {
  const k = Math.round(num(n))
  return `${k} ${k === 1 ? 'person' : 'people'}`
}

/** "Includes 1 private reader (browser privacy setting).", or null when there are none. */
export function privateReadersNote(n: number | undefined): string | null {
  const k = Math.round(num(n))
  if (k <= 0) return null
  return `Includes ${k} private ${k === 1 ? 'reader' : 'readers'} (browser privacy setting).`
}

/** Typical time cannot follow a private reader from one event to the next. */
export const TYPICAL_TIME_HINT = 'Typical time counts readers without a privacy setting.'

/** "1 sign-up", "2 sign-ups". */
export function signUps(n: number): string {
  const k = Math.round(num(n))
  return `${k} ${k === 1 ? 'sign-up' : 'sign-ups'}`
}

/** "No readers in the last 30 days." for a piece published before the window began; otherwise the first-visit text. */
export function emptyText(p: PieceMetrics, now: Date = new Date()): string {
  const at = p.published_at ? Date.parse(p.published_at) : NaN
  if (Number.isFinite(at) && now.getTime() - at > p.window_days * 86_400_000) return `No readers in the last ${p.window_days} days.`
  return EMPTY_TEXT
}

/** The server's note (piece not live): it replaces every card. */
export function noteOf(p: PieceMetrics): string | null {
  return p.note || null
}

/** True when nobody read any copy in the window. Only the windowed cards give way to the empty text. */
export function windowEmpty(p: PieceMetrics): boolean {
  return !p.note && p.copies.every((c) => num(c.metrics.people) === 0)
}

/** The Live list's narrow-screen line. */
export function liveReadersLine(r: LiveReaders | undefined | null): string {
  if (!r) return '—'
  return `${people(r.people)} · ${pctOrDash(r.finished_pct)} finished`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "29 Sep" from a YYYY-MM-DD string, with no time-zone shift. */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return `${d} ${MONTHS[m - 1]}`
}

/** Today's date in Eastern time, as YYYY-MM-DD. */
export function etToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

const DAY_MS = 86_400_000
const isoOf = (t: number): string => new Date(t).toISOString().slice(0, 10)
const utcOf = (iso: string): number => Date.parse(`${iso}T00:00:00Z`)

/** One entry per ET day from the window's first day to today, zero views where the server sent nothing.
 * The first day is clamped to when tracking began, and never later than the first day with views. */
export function fillDays(days: DayViews[], windowDays: number, trackingSince: string | null, today: string): DayViews[] {
  const end = utcOf(today)
  if (!Number.isFinite(end)) return days
  let start = end - windowDays * DAY_MS
  const tracked = trackingSince ? utcOf(trackingSince.slice(0, 10)) : NaN
  if (Number.isFinite(tracked) && tracked > start) start = tracked
  const seen = new Map(days.map((d) => [d.date, num(d.views)]))
  for (const d of days) { const t = utcOf(d.date); if (Number.isFinite(t) && t < start) start = t }
  const out: DayViews[] = []
  for (let t = start; t <= end; t += DAY_MS) { const date = isoOf(t); out.push({ date, views: seen.get(date) ?? 0 }) }
  return out
}

/** The by-day chart's aria-label, naming at most 14 days. */
export function byDayLabel(days: DayViews[]): string {
  if (days.length === 0) return 'Views by day: none'
  const shown = days.slice(-14).map((d) => `${num(d.views)} on ${dayLabel(d.date)}`)
  const more = days.length - shown.length
  return `Views by day: ${shown.join(', ')}${more > 0 ? `, and ${more} earlier day${more === 1 ? '' : 's'}` : ''}`
}

/** "Counting since 1 Oct", or null. */
export function countingSince(iso: string | null): string | null {
  return iso ? `Counting since ${dayLabel(iso.slice(0, 10))}` : null
}

const MONTH_FIRST = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number)
  return !y || !m || !d ? iso : `${MONTHS[m - 1]} ${d}`
}

/** "Oct 1 – Oct 7 · whole days, Eastern time". */
export function deskWindowText(w: DeskReaders['window']): string {
  return `${MONTH_FIRST(w.from)} – ${MONTH_FIRST(w.to)} · whole days, Eastern time`
}

/** "Today so far: 12 people". */
export function deskTodayText(t: DeskReaders['today']): string {
  return `Today so far: ${people(t.people)}`
}

/** "41% (1,310)", or "— (12)" when there is no rate. */
export function rateText(rate: number | null | undefined, count: number): string {
  return `${rate === null || rate === undefined ? '—' : `${Math.round(num(rate) * 100)}%`} (${Math.round(num(count)).toLocaleString('en-US')})`
}

export type DeskSegment = { key: 'anonymous' | 'known' | 'member'; label: string; count: number; pct: number }
const DESK_GROUPS: { key: DeskSegment['key']; label: string }[] = [
  { key: 'anonymous', label: 'Anonymous' }, { key: 'known', label: 'Known (newsletter, account)' }, { key: 'member', label: 'Members' },
]

/** The desk's who-reads-it bar: whole-number shares that sum to exactly 100 (largest remainder), or none. */
export function deskWho(who: DeskTotals['who']): DeskSegment[] {
  const counts = DESK_GROUPS.map((g) => Math.max(0, num(who[g.key])))
  const total = counts.reduce((a, b) => a + b, 0)
  if (total === 0) return []
  const exact = counts.map((c) => (c * 100) / total)
  const pcts = exact.map(Math.floor)
  let left = 100 - pcts.reduce((a, b) => a + b, 0)
  const order = exact.map((e, i) => ({ i, r: e - Math.floor(e) })).sort((a, b) => b.r - a.r || a.i - b.i)
  for (const { i } of order) { if (left <= 0) break; if (counts[i] > 0) { pcts[i] += 1; left -= 1 } }
  return DESK_GROUPS.map((g, i) => ({ key: g.key, label: g.label, count: counts[i], pct: pcts[i] })).filter((s) => s.count > 0)
}

/** The Live list's last column header: all desk rows, none, or a mix. */
export function liveGainHeader(rows: { readers: { source?: 'desk' | 'raw' } }[]): string {
  const desk = rows.filter((r) => r.readers.source === 'desk').length
  if (rows.length > 0 && desk === rows.length) return 'Members gained'
  return desk === 0 ? 'Sign-ups' : 'Sign-ups / members gained'
}

/** "1 member gained", "1.5 members gained". */
export function membersGainedText(n: number): string {
  const k = Math.round(num(n) * 10) / 10
  return `${k} ${k === 1 ? 'member' : 'members'} gained`
}
